import { useEffect, useState } from "react";
import { Loader2, RotateCcw, Save, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { useContent, PICKER_DEFAULTS } from "../../context/ContentContext";
import { MODES, MODE_META, MODE_TEXT_DEFAULTS } from "../../lib/modes";
import { ALL_PLAN_DEFAULTS, PLAN_GROUPS } from "../../lib/plans";
import { JUNIOR_HOW_DEFAULTS } from "../../lib/juniorHow";
import { JuniorHowEditor } from "./JuniorHowEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (text) => text.split("\n").map((l) => l.trim()).filter(Boolean);

// Bullet preview reuses the exact list styles of the live pages so admins see what students will see
const ModeBullets = ({ points, meta }) => (
  <ul className={`space-y-1.5 text-sm ${meta.text}`} data-testid="content-mode-bullets-preview">
    {points.map((p, i) => (
      <li key={`${p}-${i}`} className="flex items-start gap-2 text-foreground">
        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${meta.solid}`} />
        {p}
      </li>
    ))}
  </ul>
);

const PlanBullets = ({ features }) => (
  <ul className="space-y-2" data-testid="content-plan-bullets-preview">
    {features.map((f, i) => (
      <li key={`${f}-${i}`} className="flex items-start gap-2">
        <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
        <span className="text-sm">{f}</span>
      </li>
    ))}
  </ul>
);

const SectionCard = ({ title, description, children, onSave, onReset, saving, testId }) => (
  <div className="rounded-2xl border border-border bg-card p-5 space-y-5" data-testid={testId}>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h3 className="font-heading font-bold text-lg">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={onReset} disabled={saving} data-testid={`${testId}-reset`}>
          <RotateCcw className="w-3.5 h-3.5" /> Reset to defaults
        </Button>
        <Button size="sm" className="gap-1.5 rounded-xl" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </Button>
      </div>
    </div>
    {children}
  </div>
);

export const ContentTab = () => {
  const { content, setContent } = useContent();
  const [picker, setPicker] = useState(PICKER_DEFAULTS);
  const [modes, setModes] = useState({});
  const [plans, setPlans] = useState({});
  const [juniorHow, setJuniorHow] = useState({});
  const [saving, setSaving] = useState(null);

  // Editor state = defaults with saved overrides on top (re-synced whenever the server copy changes)
  useEffect(() => {
    setPicker({ ...PICKER_DEFAULTS, ...(content.picker || {}) });
    setJuniorHow({ ...JUNIOR_HOW_DEFAULTS, ...(content.junior_how || {}) });
    setModes(Object.fromEntries(MODES.map((m) => {
      const o = (content.modes || {})[m] || {};
      const d = MODE_TEXT_DEFAULTS[m];
      return [m, { label: o.label || d.label, short: o.short || d.short, tagline: o.tagline || d.tagline, points: toLines(o.points?.length ? o.points : d.points) }];
    })));
    setPlans(Object.fromEntries(Object.entries(ALL_PLAN_DEFAULTS).map(([k, p]) => {
      const o = (content.plans || {})[k] || {};
      return [k, { name: o.name || p.name, features: toLines(o.features?.length ? o.features : p.features), popular: typeof o.popular === "boolean" ? o.popular : p.popular }];
    })));
  }, [content]);

  const save = async (section, body) => {
    setSaving(section);
    try {
      const res = await fetch(`${API}/admin/content`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ [section]: body }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Could not save"); return; }
      setContent(data);
      toast.success("Saved - live on the site now");
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setSaving(null);
    }
  };

  const reset = async (section) => {
    if (!window.confirm("Reset this section to the built-in text?")) return;
    setSaving(section);
    try {
      const res = await fetch(`${API}/admin/content/${section}`, { method: "DELETE", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Could not reset"); return; }
      setContent(data);
      toast.success("Back to the default text");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="content-tab">
      <SectionCard title="Intro page" description="The full-screen 'How do you want to code?' screen new visitors see first." testId="content-picker"
        saving={saving === "picker"} onReset={() => reset("picker")} onSave={() => save("picker", picker)}>
        <div className="grid gap-4 md:grid-cols-3">
          {[["eyebrow", "Small line above the heading - [text](https://…) makes a link"], ["heading", "Heading"], ["subheading", "Sub-heading"]].map(([f, label]) => (
            <div key={f} className="space-y-1.5">
              <Label htmlFor={`picker-${f}`}>{label}</Label>
              <Input id={`picker-${f}`} value={picker[f] || ""} onChange={(e) => setPicker((p) => ({ ...p, [f]: e.target.value }))} data-testid={`content-picker-${f}`} />
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Mode cards" description="Title, short name (header toggle), tagline and bullet points for each coding mode. Renaming a mode applies everywhere it is shown." testId="content-modes"
        saving={saving === "modes"} onReset={() => reset("modes")}
        onSave={() => save("modes", Object.fromEntries(MODES.map((m) => [m, { ...modes[m], points: fromLines(modes[m].points) }])))}>
        <div className="grid gap-4 lg:grid-cols-3">
          {MODES.map((m) => {
            const meta = MODE_META[m];
            const Icon = meta.icon;
            const v = modes[m] || { label: "", short: "", tagline: "", points: "" };
            const set = (f, val) => setModes((prev) => ({ ...prev, [m]: { ...prev[m], [f]: val } }));
            return (
              <div key={m} className={`rounded-2xl border-2 p-4 space-y-3 ${meta.border}`} data-testid={`content-mode-${m}`}>
                <div className={`inline-flex items-center gap-2 font-semibold ${meta.text}`}><Icon className="w-4 h-4" /> {MODE_TEXT_DEFAULTS[m].label}</div>
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <div className="space-y-1"><Label>Title</Label><Input value={v.label} onChange={(e) => set("label", e.target.value)} data-testid={`content-mode-${m}-label`} /></div>
                  <div className="space-y-1 w-28"><Label>Short name</Label><Input value={v.short} onChange={(e) => set("short", e.target.value)} data-testid={`content-mode-${m}-short`} /></div>
                </div>
                <div className="space-y-1"><Label>Tagline</Label><Textarea rows={3} value={v.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid={`content-mode-${m}-tagline`} /></div>
                <div className="space-y-1">
                  <Label>Bullet points <span className="text-muted-foreground text-xs">(one per line)</span></Label>
                  <Textarea rows={5} value={v.points} onChange={(e) => set("points", e.target.value)} data-testid={`content-mode-${m}-points`} />
                </div>
                <div className="rounded-xl bg-muted/40 p-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Preview</p>
                  <ModeBullets points={fromLines(v.points)} meta={meta} />
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard title="How it works (Junior Blocks)" description="The rules panel students see beside a Junior grid. Each line has its own box and only appears when the grid uses that feature - so text for rocks, tunnels, loops, sensors, variables and functions can be tuned separately." testId="content-junior-how-card"
        saving={saving === "junior_how"} onReset={() => reset("junior_how")}
        onSave={() => save("junior_how", Object.fromEntries(Object.entries(juniorHow).filter(([k, v]) => (v ?? "").trim() && v.trim() !== JUNIOR_HOW_DEFAULTS[k])))}>
        <JuniorHowEditor value={juniorHow} onChange={setJuniorHow} />
      </SectionCard>

      <SectionCard title="Plans" description="Plan names, feature bullets and the 'Most Popular' badge. Prices come from Stripe and can't be edited here." testId="content-plans"
        saving={saving === "plans"} onReset={() => reset("plans")}
        onSave={() => save("plans", Object.fromEntries(Object.entries(plans).map(([k, p]) => [k, { ...p, features: fromLines(p.features) }])))}>
        {PLAN_GROUPS.map((group) => (
          <div key={group.id} className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">{group.title}</h4>
            <div className={`grid gap-4 ${group.keys.length === 3 ? "lg:grid-cols-3" : "md:grid-cols-2"}`}>
              {group.keys.map((k) => {
                const d = ALL_PLAN_DEFAULTS[k];
                const v = plans[k] || { name: "", features: "", popular: false };
                const set = (f, val) => setPlans((prev) => ({ ...prev, [k]: { ...prev[k], [f]: val } }));
                return (
                  <div key={k} className={`rounded-2xl border-2 p-4 space-y-3 ${v.popular ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`content-plan-${k}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground"><d.icon className="w-4 h-4" /> {d.price === 0 ? "Free" : `$${d.price}/${d.period}`}</div>
                      <label className="inline-flex items-center gap-1.5 text-xs font-medium">
                        <input type="checkbox" checked={!!v.popular} onChange={(e) => set("popular", e.target.checked)} data-testid={`content-plan-${k}-popular`} /> Most Popular badge
                      </label>
                    </div>
                    <div className="space-y-1"><Label>Plan name</Label><Input value={v.name} onChange={(e) => set("name", e.target.value)} data-testid={`content-plan-${k}-name`} /></div>
                    <div className="space-y-1">
                      <Label>Feature bullets <span className="text-muted-foreground text-xs">(one per line)</span></Label>
                      <Textarea rows={7} value={v.features} onChange={(e) => set("features", e.target.value)} data-testid={`content-plan-${k}-features`} />
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Preview</p>
                      <PlanBullets features={fromLines(v.features)} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </SectionCard>
    </div>
  );
};
